import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, BookOpen, Star, Quote } from 'lucide-react';
import { Button } from '../components/ui/button';
import BookCard from '../components/BookCard';
import { SITE, BOOKS, REVIEWS, ABOUT } from '../mock/mock';

const Home = () => {
  const featured = BOOKS.filter((b) => b.featured);
  const showcase = BOOKS.slice(0, 4);

  return (
    <div className="fade-in">
      {/* Hero */}
      <section
        className="relative overflow-hidden bg-brand-ink"
        style={{
          backgroundImage:
            'linear-gradient(180deg, rgba(20,16,10,0.82) 0%, rgba(26,20,12,0.68) 45%, rgba(26,20,12,0.86) 100%), url(https://images.unsplash.com/photo-1547555999-14e818e09e33)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
        }}
      >
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-28 md:py-36 text-center relative z-10">
          <span className="inline-flex items-center gap-2 text-xs md:text-sm uppercase tracking-luxe text-brand-gold font-sans mb-6 fade-up">
            <BookOpen className="h-4 w-4" /> A Home For Meaningful Non-Fiction
          </span>
          <h1 className="font-serif text-5xl md:text-8xl font-bold tracking-wide text-cream fade-up drop-shadow-lg">
            BEYOND THE PAGES
          </h1>
          <p className="mt-8 max-w-2xl mx-auto font-body text-xl md:text-2xl font-medium text-brand-goldlight leading-relaxed fade-up">
            {SITE.intro}
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4 fade-up">
            <Link to="/buy">
              <Button className="bg-brand-gold hover:bg-brand-brass text-brand-ink hover:text-white rounded-full h-12 px-8 text-base gap-2 shadow-lg">
                Explore the Collection <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            <Link to="/about">
              <Button variant="outline" className="border-cream/70 text-cream hover:bg-cream hover:text-brand-ink rounded-full h-12 px-8 text-base bg-white/5 backdrop-blur-sm">
                Our Story
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="bg-brand-ink text-cream">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { n: '7+', l: 'Published Titles' },
            { n: '900+', l: 'Readers Joined' },
            { n: '100%', l: 'Non-Fiction' },
            { n: '4.9★', l: 'Avg. Rating' },
          ].map((s) => (
            <div key={s.l}>
              <div className="font-serif text-3xl md:text-4xl font-bold text-brand-gold">{s.n}</div>
              <div className="text-sm text-white/70 mt-1">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Featured book */}
      {featured.map((book) => (
        <section key={book.id} className="max-w-7xl mx-auto px-5 lg:px-8 py-20">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="absolute -inset-4 bg-sand rounded-3xl -rotate-2" />
              <img
                src={book.front}
                alt={book.title}
                className="relative w-full max-w-sm mx-auto rounded-lg shadow-2xl"
              />
            </div>
            <div>
              <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-brand-brass font-semibold">
                <Sparkles className="h-4 w-4" /> Bestseller
              </span>
              <h2 className="font-serif text-4xl md:text-5xl font-bold text-brand-ink mt-3">{book.title}</h2>
              <p className="font-sans text-lg italic text-brand-brass mt-1">{book.subtitle}</p>
              <p className="text-brand-brown/90 leading-relaxed mt-5 text-lg">{book.blurb}</p>
              <div className="mt-8">
                <Link to="/buy">
                  <Button className="bg-brand-gold hover:bg-brand-brass text-brand-ink hover:text-white rounded-full h-12 px-8 gap-2">
                    <BookOpen className="h-4 w-4" /> Grab Your Copy
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Collection showcase */}
      <section className="bg-sand/50">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20">
          <div className="text-center mb-12">
            <h2 className="font-serif text-4xl font-bold text-brand-ink">From the Collection</h2>
            <div className="section-divider mx-auto mt-4" />
            <p className="text-brand-brown/80 mt-4 max-w-xl mx-auto">
              Thought-provoking non-fiction exploring identity, truth, culture, power, and the human condition.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {showcase.map((b) => (
              <BookCard key={b.id} book={b} />
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/buy">
              <Button variant="outline" className="border-brand-gold text-brand-ink hover:bg-brand-gold hover:text-white rounded-full h-12 px-8 gap-2">
                View All Books <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Mission teaser */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <img src={ABOUT.hero} alt="About" className="w-full h-80 object-cover rounded-2xl shadow-lg" />
          <div>
            <h2 className="font-serif text-4xl font-bold text-brand-ink">Read clearly. Think deeply.</h2>
            <div className="section-divider mt-4" />
            <p className="text-brand-brown/90 leading-relaxed mt-6 text-lg">
              Experience the fusion of imagination and expertise with Limpid Reader—the catalyst for
              broader thinking and understanding about the world around us.
            </p>
            <Link to="/services">
              <Button className="mt-8 bg-brand-ink hover:bg-brand-brown text-cream rounded-full h-12 px-8 gap-2">
                Our Services <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="bg-sand/50">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20">
          <div className="text-center mb-12">
            <h2 className="font-serif text-4xl font-bold text-brand-ink">What Readers Say</h2>
            <div className="section-divider mx-auto mt-4" />
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {REVIEWS.map((r) => (
              <div key={r.name} className="bg-white rounded-2xl p-7 shadow-md relative">
                <Quote className="h-8 w-8 text-brand-goldlight absolute top-5 right-5" />
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: r.rating }).map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-brand-gold text-brand-gold" />
                  ))}
                </div>
                <p className="text-brand-brown/90 leading-relaxed text-sm">“{r.text}”</p>
                <div className="mt-5">
                  <p className="font-serif font-bold text-brand-ink">{r.name}</p>
                  <p className="text-xs text-brand-brass italic">on {r.book}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
